import React, { useState, useRef, useEffect } from 'react';
import { useChartSetup } from '../../hooks/useChartSetup';
import { useDrawingTools } from '../../hooks/useDrawingTools';
import { useOverlayLines } from '../../hooks/useOverlayLines';
import { useIndicators } from '../../hooks/useIndicators';
import ChartOverlay from './ChartOverlay';
import ChartTooltip from './ChartTooltip';
import DrawingToolbar from './DrawingToolbar';
import MeasurePopup from './MeasurePopup';
import TrendlinePopup from './TrendlinePopup';

const CandlestickChart = ({
  coin, timeframe, onPriceUpdate, stopLoss, takeProfit, activeLimitOrders = [],
  chartType = 'Candlestick', showVolume = false, activeIndicators = {},
  onChartTypeChange, onToggleIndicator, onClearIndicators,
  onToggleVolume, trades = [],
}) => {
  const [crosshairData, setCrosshairData] = useState(null);
  const [crosshairPos, setCrosshairPos] = useState(null);

  const { chartRef, seriesRef, chartContainerRef, loading, error, chartVersion, data } = useChartSetup({
    coin, timeframe, onPriceUpdate, chartType, showVolume,
  });

  const drawing = useDrawingTools({ chartRef, seriesRef, chartVersion });
  useOverlayLines({ seriesRef, stopLoss, takeProfit, activeLimitOrders, coin, loading });
  useIndicators({ chartRef, chartVersion, data, activeIndicators });

  const containerRef = useRef(null);

  // Crosshair tooltip subscription
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    const onCrosshair = (param) => {
      if (!param.time || !param.point) {
        setCrosshairData(null);
        return;
      }
      const cd = data.find(d => d.time === param.time);
      if (cd) {
        setCrosshairData(cd);
        setCrosshairPos({ x: param.point.x, y: param.point.y, containerW: chartContainerRef.current?.clientWidth });
      }
    };
    chart.subscribeCrosshairMove(onCrosshair);
    return () => chart.unsubscribeCrosshairMove(onCrosshair);
  }, [chartRef, seriesRef, data, chartVersion, chartContainerRef]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore shortcuts if the user is typing in an input field
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      // Drawing Tool Shortcuts (Alt + Letter)
      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 't': e.preventDefault(); drawing.handleToolSelect('trend'); break; // Alt + T: Trendline
          case 'h': e.preventDefault(); drawing.handleToolSelect('horizontal'); break; // Alt + H: Horizontal Line
          case 'm': e.preventDefault(); drawing.handleToolSelect('measure'); break; // Alt + M: Measure
          case 'c': e.preventDefault(); drawing.clearAllDrawings(); break; // Alt + C: Clear Drawings
          default: break;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawing]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 'auto',
      }}
    >
      <ChartOverlay loading={loading} error={error} coin={coin} />

      <div className="drawing-toolbar">
        <DrawingToolbar
          drawingTool={drawing.drawingTool}
          onToolSelect={drawing.handleToolSelect}
          onClearAll={drawing.clearAllDrawings}
        />
      </div>

      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          borderRadius: '6px',
          overflow: 'hidden',
          cursor: drawing.isHoveringTrendline && !drawing.drawingTool
            ? 'pointer'
            : drawing.drawingTool ? 'crosshair' : 'default',
        }}
      />

      <ChartTooltip
        data={crosshairData}
        visible={!!crosshairData}
        pos={crosshairPos}
      />

      <MeasurePopup
        data={drawing.measureData}
        liveData={drawing.liveMeasureData}
        containerWidth={chartContainerRef.current?.clientWidth}
      />

      <TrendlinePopup
        selectedTL={drawing.selectedTL}
        containerWidth={chartContainerRef.current?.clientWidth}
        showStyleOptions={drawing.showStyleOptions}
        onColorChange={drawing.updateTLColor}
        onLineStyleChange={drawing.updateTLLineStyle}
        onRemove={drawing.removeTL}
        onShowStyles={() => drawing.setShowStyleOptions(true)}
        onClose={() => drawing.setShowStyleOptions(false)}
      />
    </div>
  );
};

export default CandlestickChart;
